/**
 * Scanner PIN. The screen going dark is a visibility hide; opening it
 * again is a show. After a short hide we ask for a team PIN. Switch
 * user uses the same pad and can be a different employee.
 */

import { staffIdleLockDisplayName } from "./staffIdleLock";
import {
  isFullAppSearch,
  isHandheldSearch,
  isHandheldStaffUser,
  isHandheldTaskPath,
  isPhoneClassScreen,
  isWarehouseScannerDevice,
  readScannerHomePref,
} from "./handheldMode";

export const HANDHELD_PIN_KEY = "riverr_handheld_pin_v1";
export const HANDHELD_ENTERPRISE_KEY = "riverr_handheld_enterprise_v1";
export const HANDHELD_WAKE_LOCK_MS = 1500;
export const HANDHELD_SWITCH_EVENT = "riverr-handheld-switch-user";

/** First token of the signed-in name, for the gun header. */
export function handheldFirstName(user) {
  const explicit = String(user?.firstName || "").trim();
  if (explicit) return explicit;
  const display = String(staffIdleLockDisplayName(user) || "").trim();
  if (!display || display === "Team member") return "";
  return display.split(/\s+/)[0];
}

export function handheldWelcomeTitle(user) {
  const name = handheldFirstName(user);
  if (!user?.uid || !name) return "Welcome";
  return `Welcome, ${name}`;
}

export function isHandheldPinSession({ pathname = "", search = "" } = {}) {
  if (isFullAppSearch(search)) return false;
  return isHandheldTaskPath(pathname) || isHandheldSearch(search);
}

/** Env snapshot for PIN-login routing (logout, /login bounce, auth guard). */
export function handheldPinLoginEnv() {
  if (typeof window === "undefined") {
    return {
      pathname: "",
      search: "",
      userAgent: "",
      screenWidth: 0,
      screenHeight: 0,
      innerWidth: 0,
      innerHeight: 0,
      devicePixelRatio: 1,
    };
  }
  return {
    pathname: window.location.pathname || "",
    search: window.location.search || "",
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    screenWidth: window.screen?.width,
    screenHeight: window.screen?.height,
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio,
  };
}

/**
 * Gun PIN pad instead of email login. Admins and staff on a warehouse
 * PDA qualify. A seller account never does, including /handheld.
 */
export function shouldUseHandheldPinLogin({
  pathname = "",
  search = "",
  userAgent = "",
  screenWidth,
  screenHeight,
  innerWidth,
  innerHeight,
  devicePixelRatio = 1,
  pref,
  user,
} = {}) {
  if (user?.uid && !isHandheldStaffUser(user)) return false;
  if (
    isWarehouseScannerDevice({
      userAgent,
      screenWidth,
      screenHeight,
      innerWidth,
      innerHeight,
      devicePixelRatio,
    })
  ) {
    return true;
  }
  const remembered = pref === undefined ? readScannerHomePref() : pref;
  // A handset that was misread as a gun got this pref on logout.
  // Keep the email login; only a real PDA should stick on the PIN pad.
  if (
    remembered === "on" &&
    !isPhoneClassScreen({ screenWidth, screenHeight, devicePixelRatio })
  ) {
    return true;
  }
  if (isFullAppSearch(search)) return false;
  if (isHandheldSearch(search)) return true;
  // /handheld alone must not replace email login. That trapped sellers
  // who were sent there. Only a known admin/staff session stays on the pad.
  if (isHandheldStaffUser(user) && isHandheldTaskPath(pathname)) return true;
  return false;
}

export function rememberHandheldEnterprise(enterpriseId) {
  const id = String(enterpriseId || "").trim();
  if (!id || typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(HANDHELD_ENTERPRISE_KEY, id);
  } catch {
    /* ignore quota / private mode */
  }
}

export function readHandheldEnterprise() {
  if (typeof localStorage === "undefined") return "";
  try {
    return String(localStorage.getItem(HANDHELD_ENTERPRISE_KEY) || "").trim();
  } catch {
    return "";
  }
}

/** True when the gun was asleep long enough that the next open needs a PIN. */
export function shouldLockOnResume(hiddenAt, now, minMs = HANDHELD_WAKE_LOCK_MS) {
  const hidden = Number(hiddenAt);
  if (!Number.isFinite(hidden) || hidden <= 0) return false;
  return now - hidden >= minMs;
}

export function requestHandheldSwitchUser() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(HANDHELD_SWITCH_EVENT));
}

export function readHandheldPin() {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const parsed = JSON.parse(sessionStorage.getItem(HANDHELD_PIN_KEY) || "null");
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeHandheldPin(state) {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(HANDHELD_PIN_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota / private mode */
  }
}

export function clearHandheldPinLock() {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(HANDHELD_PIN_KEY);
  } catch {
    /* ignore */
  }
}
